
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff } from "lucide-react";
import { useViewAs } from "@/contexts/ViewAsContext";
import { toast } from "@/components/ui/use-toast";

export function ViewAsButton() {
  const { viewAsUserId, setViewAsUser, viewingAs } = useViewAs();

  const { data: users } = useQuery({
    queryKey: ['all-users'],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .order('first_name');
      return data || [];
    }
  });

  const handleViewAs = (userId: string | null) => {
    setViewAsUser(userId);
    if (userId) {
      const user = users?.find(u => u.id === userId);
      toast({
        title: "Viewing as user",
        description: `Now viewing as ${user?.first_name} ${user?.last_name}`,
      });
    } else {
      toast({
        title: "Returned to admin view",
        description: "You're now viewing with your normal permissions",
      });
    }
  };

  if (!users?.length) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {viewAsUserId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {viewingAs 
            ? `Viewing as ${viewingAs.first_name} ${viewingAs.last_name}`
            : "View as user"
          }
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {viewAsUserId && (
          <DropdownMenuItem onClick={() => handleViewAs(null)}>
            Return to admin view
          </DropdownMenuItem>
        )}
        {users.map((user) => (
          <DropdownMenuItem
            key={user.id}
            onClick={() => handleViewAs(user.id)}
            disabled={viewAsUserId === user.id}
          >
            {user.first_name} {user.last_name} ({user.email})
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
